import React, { useMemo, memo, useCallback } from "react";
import countries from "i18n-iso-countries";
import { FixedSizeList as List } from "react-window";
import { MdSearch, MdArrowBack } from "react-icons/md";
import { FaChevronRight } from "react-icons/fa";

try { countries.registerLocale(require("i18n-iso-countries/langs/en.json")); } catch {}

const ROW_HEIGHT = 80;

const normalizeCountryCode = (value) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (/^[a-z]{2}$/i.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  try {
    const code = countries.getAlpha2Code(trimmed, "en");
    return code ? code.toLowerCase() : null;
  } catch {
    return null;
  }
};

const getCountryLabel = (value) => {
  const code = normalizeCountryCode(value);
  if (!code) return value || "";
  try {
    return countries.getName(code.toUpperCase(), "en") || code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

const CountryFlag = memo(({ country }) => {
  const code = normalizeCountryCode(country);
  return code ? (
    <img
      src={`https://flagcdn.com/24x18/${code}.png`}
      alt={country}
      className="rounded"
      loading="lazy"
      width={24}
      height={18}
    />
  ) : (
    <span className="text-body-secondary small">🌿</span>
  );
});

function Row({ index, style, data }) {
  const person = data.items[index];
  const countryLabel = getCountryLabel(person.country);
  return (
    <div style={style} className="mt-1">
      <button
        type="button"
        className="ui-option ui-option--blue w-100 text-start p-2"
        onClick={() => {
          data.onSelect(person);
          data.setSearchedTerm("");
        }}
      >
        <div className="ui-option__icon" style={{ padding: 0 }}>
          <CountryFlag country={person.country} />
        </div>
        <div className="ui-option__text">
          <div className="ui-option__title">
            {person.firstName} {person.lastName || ""}
          </div>
          {!!countryLabel && (
            <div className="ui-option__subtitle">{countryLabel}</div>
          )}
        </div>
        <div className="ui-option__chevron"><FaChevronRight /></div>
      </button>
    </div>
  );
}

function SearchListStep({ data, searchedTerm, setSearchedTerm, onSelect, onBack }) {
  const term = (searchedTerm || "").toLowerCase().trim();

  const filtered = useMemo(() => {
    if (!term) return data || [];
    return (data || []).filter((p) =>
      `${p.firstName ?? ""} ${p.lastName ?? ""}`.toLowerCase().includes(term)
    );
  }, [data, term]);

  const onChange = useCallback((e) => setSearchedTerm(e.target.value), [setSearchedTerm]);

  return (
    <section className="ui-panel">
      <header className="ui-panel__header">
        <div className="d-flex align-items-center justify-content-between">
          <h3 className="ui-title mb-0">Choose Person</h3>
          <span className="badge bg-body-tertiary border text-body">
            {filtered.length} result{filtered.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="mt-3">
          <div className="input-group">
            <span className="input-group-text bg-body"><MdSearch /></span>
            <input
              type="text"
              className="form-control bg-body"
              placeholder="Search by name…"
              value={searchedTerm}
              onChange={onChange}
              autoComplete="off"
            />
          </div>
        </div>
      </header>

      <div className="mt-2">
        <List
          height={400}
          width="100%"
          itemCount={filtered.length}
          itemSize={ROW_HEIGHT}
          itemData={{ items: filtered, onSelect, setSearchedTerm }}
        >
          {Row}
        </List>
      </div>

      <div className="ui-actions">
        <button type="button" className="ui-btn" onClick={onBack}>
          <MdArrowBack /> Back
        </button>
      </div>
    </section>
  );
}

export default memo(SearchListStep);
